import { useEffect, useState } from 'react';
import { TrendingUp, DollarSign, ShoppingCart, Percent } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { EarningsAPI, EarningsStatistics } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

export default function EarningsStatisticsPage() {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<EarningsStatistics | null>(null);
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await EarningsAPI.getStatistics({ period });
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching earnings statistics:', err);
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error || !statistics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load statistics'}</p>
          <Button onClick={fetchStatistics}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Earnings Statistics</h1>
          <p className="text-muted-foreground">Detailed platform earnings analytics</p>
        </div>
        <div className="flex gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
            className="px-4 py-2 border rounded-md"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Platform Earnings</p>
                <p className="text-2xl font-bold">{formatCurrency(statistics.totalPlatformEarnings)}</p>
                <p className="text-xs text-green-600 mt-1">
                  {statistics.earningsPercentage.toFixed(2)}% of order value
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Order Value</p>
                <p className="text-2xl font-bold">{formatCurrency(statistics.totalOrderValue)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {statistics.totalOrders} orders
                </p>
              </div>
              <ShoppingCart className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Order Earnings</p>
                <p className="text-2xl font-bold">{formatCurrency(statistics.averageOrderEarnings)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Per transaction
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Earnings Rate</p>
                <p className="text-2xl font-bold">{statistics.earningsPercentage.toFixed(2)}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Commission rate
                </p>
              </div>
              <Percent className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Distribution */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Payment Distribution</CardTitle>
            <CardDescription>Breakdown of order value distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Supplier Payments</span>
                  <span className="text-sm font-semibold">{formatCurrency(statistics.totalSupplierPayments)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{
                      width: `${(statistics.totalSupplierPayments / statistics.totalOrderValue) * 100}%`
                    }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {((statistics.totalSupplierPayments / statistics.totalOrderValue) * 100).toFixed(2)}% of total
                </p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Transporter Payments</span>
                  <span className="text-sm font-semibold">{formatCurrency(statistics.totalTransporterPayments)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{
                      width: `${(statistics.totalTransporterPayments / statistics.totalOrderValue) * 100}%`
                    }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {((statistics.totalTransporterPayments / statistics.totalOrderValue) * 100).toFixed(2)}% of total
                </p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Platform Earnings</span>
                  <span className="text-sm font-semibold text-green-600">{formatCurrency(statistics.totalPlatformEarnings)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{
                      width: `${statistics.earningsPercentage}%`
                    }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {statistics.earningsPercentage.toFixed(2)}% of total
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Transaction Metrics</CardTitle>
            <CardDescription>Order and transaction statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex justify-between items-center pb-4 border-b">
                <div>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="text-3xl font-bold">{statistics.totalOrders}</p>
                </div>
                <ShoppingCart className="h-10 w-10 text-blue-600" />
              </div>

              <div className="flex justify-between items-center pb-4 border-b">
                <div>
                  <p className="text-sm text-muted-foreground">Total Transactions</p>
                  <p className="text-3xl font-bold">{statistics.totalTransactions}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-600" />
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Average Order Value</p>
                  <p className="text-3xl font-bold">
                    {formatCurrency(statistics.totalOrderValue / statistics.totalOrders || 0)}
                  </p>
                </div>
                <DollarSign className="h-10 w-10 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
          <CardDescription>Overall earnings performance for {period} period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Revenue Generated</p>
              <p className="text-2xl font-bold">{formatCurrency(statistics.totalOrderValue)}</p>
              <p className="text-sm text-muted-foreground">
                From {statistics.totalOrders} completed orders
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Total Payouts</p>
              <p className="text-2xl font-bold">
                {formatCurrency(statistics.totalSupplierPayments + statistics.totalTransporterPayments)}
              </p>
              <p className="text-sm text-muted-foreground">
                To suppliers and transporters
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Net Platform Earnings</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(statistics.totalPlatformEarnings)}
              </p>
              <p className="text-sm text-muted-foreground">
                {statistics.earningsPercentage.toFixed(2)}% commission rate
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
